import React, {useState} from "react"
import {scan} from "react-scan"
import * as Sentry from "@sentry/react"
import {browserTracingIntegration} from "@sentry/react"
import ReactDOM from "react-dom/client"
import {detect} from "detect-browser"
import {BrowserRouter as Router} from "react-router-dom"
import {IntlProvider} from "react-intl"
import {Media, MediaContextProvider} from "./utils/media-utils"
import {App as DesktopApp} from "./app"
import {App as MobileApp} from "./app-mobile"
import {getI18nMessages, Locale} from "./locale"
import "semantic-ui-css/semantic.min.css"
import "./styles/app.css"


scan({
    enabled: process.env.REACT_APP_ENVIRONMENT !== "production",
    showToolbar: process.env.REACT_APP_ENVIRONMENT !== "production",
    trackUnnecessaryRenders: true,
    log: false
})

/**
 * Initialize logs backend.
 */
console.log("Starting app @", process.env.REACT_APP_ENVIRONMENT)
Sentry.init({
    environment: process.env.REACT_APP_ENVIRONMENT || "production",
    dsn: "https://aee5116bb074c58422bfb58ce53e298f@o4510001008148480.ingest.de.sentry.io/4510001015685200",
    integrations: [
        browserTracingIntegration(),
        Sentry.feedbackIntegration({ colorScheme: "system", autoInject: false }),
        Sentry.featureFlagsIntegration()
    ],
    sendDefaultPii: true  // Setting this option to true will send default PII data to Sentry
})

/**
 * Initialize feature-flags backend.
 */
const flagsIntegration = Sentry.getClient()?.getIntegrationByName<Sentry.FeatureFlagsIntegration>("FeatureFlags")
flagsIntegration!.addFeatureFlag("payments", true)

// Initial inject location.pathname for conditional CSS
document.documentElement.setAttribute("data-path", window.location.pathname)
// Patch pushState
const originalPushState = window.history.pushState
window.history.pushState = function (state, title, url) {
    originalPushState.apply(this, [state, title, url])
    document.documentElement.setAttribute("data-path", window.location.pathname)
}
// Catch back/forward buttons
window.addEventListener("popstate", () => {
    document.documentElement.setAttribute("data-path", window.location.pathname)
})

const rootElement = document.getElementById("root")
ReactDOM.createRoot(rootElement).render(
    detect()?.name === "ie"
        ? <p>GEDCOM Creator does not support Internet Explorer. Please try a modern browser.</p>
        : <Root/>
)

// Polyfill global for libraries that expect Node.js
if (typeof global === "undefined") window.global = window
function Root() {
    // i18n priorities: #1 user selected language, #2 browser language, #3 EN locale
    const [i18nLanguage, setI18nLanguage] = useState(sessionStorage.getItem("i18nLanguage") || navigator.language?.split(/[-_]/)[0] || Locale.EN)
    return (
        <IntlProvider locale={i18nLanguage} messages={getI18nMessages(i18nLanguage)}>
            <MediaContextProvider>
                <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    {/* Mobile portrait */}
                    <Media lessThan="mobilePortrait"><MobileApp setI18nLanguage={setI18nLanguage}/></Media>
                    {/* Mobile landscape */}
                    <Media greaterThanOrEqual="mobilePortrait" lessThan="mobileLandscape"><MobileApp setI18nLanguage={setI18nLanguage}/></Media>
                    {/* Desktop */}
                    <Media greaterThanOrEqual="desktop"><DesktopApp setI18nLanguage={setI18nLanguage}/></Media>
                </Router>
            </MediaContextProvider>
        </IntlProvider>
    )
}

/**
 * Suppress libraries internal warnings globally.
 */
const consoleError = console.error
console.error = (...args: any[]) => {
    if (typeof args[0] === "string") {
        // Suppress
        if (args[0].includes("findDOMNode is deprecated")) return
        if (args[0].includes("Support for defaultProps will be removed")) return
        if (args[0].includes("Each child in a list should have a unique")) return
    }
    consoleError(...args)  // Keep all other errors
}